/**
 * Minimal dense linear algebra. Only what the calibration fit needs; the
 * systems involved are 3x3, so there is no case for pulling in a library.
 */

/**
 * Solve a square system A x = b by Gauss-Jordan elimination with partial
 * pivoting. `a` is row-major n x n and is not mutated.
 *
 * Returns null when the matrix is singular to working precision, which happens
 * for real reasons here — for example if every sampled patch is the same
 * colour, so the fit is underdetermined.
 */
export function solveLinearSystem(a: number[], b: number[], n: number): number[] | null {
  const m: number[][] = [];
  for (let row = 0; row < n; row++) {
    m.push([...a.slice(row * n, row * n + n), b[row] as number]);
  }

  for (let col = 0; col < n; col++) {
    let pivot = col;
    for (let row = col + 1; row < n; row++) {
      if (Math.abs((m[row] as number[])[col] as number) > Math.abs((m[pivot] as number[])[col] as number)) {
        pivot = row;
      }
    }

    const pivotRow = m[pivot] as number[];
    const pivotValue = pivotRow[col] as number;
    if (Math.abs(pivotValue) < 1e-12) return null;

    m[pivot] = m[col] as number[];
    m[col] = pivotRow;

    for (let j = col; j <= n; j++) {
      pivotRow[j] = (pivotRow[j] as number) / pivotValue;
    }

    for (let row = 0; row < n; row++) {
      if (row === col) continue;
      const target = m[row] as number[];
      const factor = target[col] as number;
      if (factor === 0) continue;
      for (let j = col; j <= n; j++) {
        target[j] = (target[j] as number) - factor * (pivotRow[j] as number);
      }
    }
  }

  return m.map((row) => row[n] as number);
}

/**
 * Least-squares solve of an over-determined system via normal equations.
 *
 * `rows` are the observations and `targets` the corresponding outputs. Normal
 * equations square the condition number, which would be a poor choice for a
 * large or ill-conditioned system; for a 3-column fit over a handful of colour
 * patches it is entirely adequate and keeps the code readable.
 */
export function leastSquares(rows: number[][], targets: number[]): number[] | null {
  const width = rows[0]?.length ?? 0;
  if (width === 0 || rows.length !== targets.length) return null;

  const ata = new Array<number>(width * width).fill(0);
  const atb = new Array<number>(width).fill(0);

  for (let observation = 0; observation < rows.length; observation++) {
    const row = rows[observation] as number[];
    const target = targets[observation] as number;
    for (let i = 0; i < width; i++) {
      const ri = row[i] as number;
      atb[i] = (atb[i] as number) + ri * target;
      for (let j = 0; j < width; j++) {
        ata[i * width + j] = (ata[i * width + j] as number) + ri * (row[j] as number);
      }
    }
  }

  return solveLinearSystem(ata, atb, width);
}
